import React, { useState, useEffect, useRef, useMemo } from "react";
import "../../../css/Common/Pages/RosemountInMediaPage/RosemountInMedia.css";
import NavBar from "frontend/Components/DesignSystem/NavBar";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
import ButtonGreen from "frontend/Components/DesignSystem/Buttons/ButtonGreen";
import { useNavigate } from "react-router-dom";
import AdviserValues from "frontend/Components/AdviserValues/AdviserValue";
import CardProduct from "frontend/Components/DesignSystem/Cards/CardProduct";
import { ReactComponent as HearFromOurAdvisersIcon } from "../../../assets/icons/hear from our advisers.svg";
import { ReactComponent as EventsIcon } from "../../../assets/icons/Events.svg";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
import Testimonials from "../../Components/Testimonials/Testimonials";
import VideoCarouselPopup from "../../Components/DesignSystem/VideoCarousel";
import ButtonYellow from "frontend/Components/DesignSystem/Buttons/ButtonYellow";
import { doc, getDoc } from "firebase/firestore";
import { db } from "backend/firebaseConfig";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import {
  SEO_PAGES,
  SEO_SITE,
  getPageUrl,
  getLogoUrl,
} from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";
import { getAdviserValues } from "frontend/Utils/GetAdviserValues";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const PAGE_SIZE = 10;
const WINDOW_SIZE = 3;

const RosemountInMediaPage = () => {
  const navigationItems = [
    { label: "Home", navigateTo: "/Adviser" },
    { label: "Join Us", navigateTo: "/Adviser/JoinUs" },
    { label: "About Us", navigateTo: "/Adviser/AboutUs" },
    {
      label: "Contact Us",
      navigateTo: "/ContactUs",
      state: { isAdviser: true },
    },
  ];

  const altTextDictionary = {
    Cherry: "Cherry logo over two modern sky scrapers",
    "Financial%20Reporter":
      "Financial Reporter logo over two modern sky scrapers",
    "Health%20%26%20Protection":
      "Health & Protection logo over two modern sky scrapers",
    "IFA%20Magazine": "The IFA magazine logo on top of two modern sky scrapers",
    "Modern%20Lender": "Modern Lender logo over two modern sky scrapers",
    "Money%20Age": "Money Age logo over two modern sky scrapers",
    "Mortgage%20Finance%20Gazette":
      "Mortgage Finance Gazette logo over two modern sky scrapers",
    "Mortgage%20Introducer":
      "Mortgage Introducer logo over two modern sky scrapers",
    "Mortgage%20Solutions":
      "Mortgage Solutions logo over two modern sky scrapers",
    "Mortgage%20Soup": "Mortgage Soup logo over two modern sky scrapers",
    "Mortgage%20Strategy":
      "Mortgage Strategy logo over two modern sky scrapers",
    "Mortgage%20TV%20Introducer":
      "Mortgage TV Introducer logo over two modern sky scrapers",
    "Professional%20Adviser":
      "Professional Adviser logo over two modern sky scrapers",
    "Protection%20Reporter":
      "Protection Reporter logo over two modern sky scrapers",
    "The%20Intermediary": "The Intermediary logo over two modern sky scrapers",
  };

  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [values, setValues] = useState([]);
  const [media, setMedia] = useState([]);

  // pagination state
  const [page, setPage] = useState(1);
  const [windowStart, setWindowStart] = useState(1); // sticky window
  const topRef = useRef(null);
  const userPagedRef = useRef(false); // only scroll on user-initiated pagination

  // helpers
  const getAltText = (link) => {
    if (!link) return "Image related to Rosemount Financial";
    for (const keyword in altTextDictionary) {
      if (link.includes(keyword)) return altTextDictionary[keyword];
    }
    return "Image from media coverage";
  };

  const getWindowPages = (total, start, size = WINDOW_SIZE) => {
    if (total <= 0) return [];
    const maxStart = Math.max(1, total - size + 1);
    const safeStart = Math.min(Math.max(1, start), maxStart);
    const end = Math.min(total, safeStart + size - 1);
    return Array.from({ length: end - safeStart + 1 }, (_, i) => safeStart + i);
  };

  const updateWindow = (nextPage, total) => {
    const maxStart = Math.max(1, total - WINDOW_SIZE + 1);
    if (nextPage < windowStart) {
      setWindowStart(Math.max(1, nextPage));
    } else if (nextPage > windowStart + WINDOW_SIZE - 1) {
      setWindowStart(Math.min(maxStart, nextPage - WINDOW_SIZE + 1));
    }
  };

  const goPrev = () => {
    if (page === 1) return;
    userPagedRef.current = true;
    const next = page - 1;
    setPage(next);
    updateWindow(next, totalPages);
  };

  const goNext = () => {
    if (page === totalPages) return;
    userPagedRef.current = true;
    const next = page + 1;
    setPage(next);
    updateWindow(next, totalPages);
  };

  const goTo = (p) => {
    const next = Math.max(1, Math.min(totalPages, p));
    userPagedRef.current = true;
    setPage(next);
    updateWindow(next, totalPages);
  };

  // data
  useEffect(() => {
    const fetchValues = async () => {
      const data = await getAdviserValues();
      setValues(data || []);
    };
    fetchValues();
  }, []);

  const fetchMedia = async () => {
    const docRef = doc(db, "Marketing", "Media");
    const docSnap = await getDoc(docRef);
    const items = docSnap.exists() ? docSnap.data().items || [] : [];
    // (optional) if you have a date string field, sort newest first here
    setMedia(items);
    setPage(1);
    setWindowStart(1);
  };

  useEffect(() => {
    fetchMedia();
  }, []);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((media?.length || 0) / PAGE_SIZE)),
    [media]
  );

  useEffect(() => {
    // clamp if count changes
    const maxStart = Math.max(1, totalPages - WINDOW_SIZE + 1);
    setWindowStart((prev) => Math.min(prev, maxStart));
    setPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  // page slice
  const pageSlice = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return media.slice(start, start + PAGE_SIZE);
  }, [media, page]);

  // scroll-to-top only when user paged
  useEffect(() => {
    if (!userPagedRef.current) return;
    if (topRef.current) {
      const headerOffset = 88; // adjust to your NavBar height
      const y =
        topRef.current.getBoundingClientRect().top +
        window.pageYOffset -
        headerOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
    userPagedRef.current = false;
  }, [page]);

  const handleToggleModal = () => setIsModalOpen((s) => !s);
const pageUrl = getPageUrl(SEO_PAGES.rosemountInMedia.path);
const logoUrl = getLogoUrl();

const mediaStructuredData = {
  "@type": "CollectionPage",
  name: "Rosemount in the Media",
  description: SEO_PAGES.rosemountInMedia.description,
  url: pageUrl,
  about: {
    "@id": `${SEO_SITE.siteUrl}/#organization`,
  },
  mainEntity: {
    "@type": "ItemList",
    name: "Rosemount Financial Solutions Media Coverage",
    itemListElement: media.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "NewsArticle",
        headline: item.title || "Rosemount Financial Solutions Media Coverage",
        description:
          item.content && item.content.length > 160
            ? `${item.content.substring(0, 157)}...`
            : item.content || SEO_PAGES.rosemountInMedia.description,
        image: item.image || logoUrl,
        url: item.link || pageUrl,
        author: {
          "@id": `${SEO_SITE.siteUrl}/#organization`,
        },
        publisher: {
          "@id": `${SEO_SITE.siteUrl}/#organization`,
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": item.link || pageUrl,
        },
      },
    })),
  },
};
  return (
    <div className="RosemountInMediaPage">
      <SeoHelmet
        page={SEO_PAGES.rosemountInMedia}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "Rosemount in the Media",
            url: getPageUrl("/RosemountInMedia"),
          },
        ]}
        extraSchemas={[mediaStructuredData]}
      />

      <NavBar navigationItems={navigationItems} />

      <div ref={topRef} />
      <HeroAnimation imgSrc={ImageDirectory["News"]} isAdviser={true}>
        <h1>Rosemount In The Media</h1>
      </HeroAnimation>

      {/* anchor for scroll-to-top */}

      {/* 10 items per page */}
      {pageSlice.map((item, index) => {
        const globalIndex = (page - 1) * PAGE_SIZE + index;
        return (
          <div
            key={globalIndex}
            className={`RosemountMedia ${
              globalIndex % 2 === 0 ? "even-background" : "odd-background"
            }`}
          >
            <div className="RosemountInMedia">
              <div className="RosemountInMedia-Content">
                {item.title && <h2>{item.title}</h2>}
                <div className="Content-Large item-content">{item.content}</div>
                <ButtonGreen
                  buttonText="Read More"
                  onClick={() => window.open(item.link, "_blank")}
                />
              </div>
              <img
                loading="eager"
                v
                fetchPriority="high"
                src={item.image}
                alt={getAltText(item.image)}
              />
            </div>
          </div>
        );
      })}

      {/* Pagination controls (same UI as your blog page) */}
      {media.length > 0 && (
        <div className="PaginationControls">
          <button
            type="button"
            className="pagination-icon-btn"
            onClick={goPrev}
            disabled={page === 1}
            aria-label="Previous page"
          >
            <ChevronLeftIcon />
          </button>

          <div className="pagination-pages">
            {getWindowPages(totalPages, windowStart).map((p) => (
              <button
                key={p}
                type="button"
                className={`pagination-page-btn ${
                  p === page ? "is-active" : ""
                }`}
                onClick={() => goTo(p)}
                aria-current={p === page ? "page" : undefined}
                aria-label={`Page ${p}`}
              >
                <div className="Content-SemiBold">{p}</div>
              </button>
            ))}
          </div>

          <button
            type="button"
            className="pagination-icon-btn"
            onClick={goNext}
            disabled={page === totalPages}
            aria-label="Next page"
          >
            <ChevronRightIcon />
          </button>
        </div>
      )}

      <AdviserValues
        values={values}
        isAdviser={true}
        title="Your Partner in Success"
      >
        <ButtonYellow
          buttonText="Join Us"
          onClick={() => navigate("/Adviser/JoinUs")}
        />
      </AdviserValues>

      <div className="About-Us">
        <div className="Adviser-Services-Holder1">
          <div onClick={handleToggleModal}>
            <CardProduct
              Icon={HearFromOurAdvisersIcon}
              title="Hear from our Advisers"
              isAdviser={true}
            />
          </div>
          <VideoCarouselPopup
            isOpen={isModalOpen}
            onClose={handleToggleModal}
          />
          <CardProduct
            Icon={EventsIcon}
            title="Events"
            isAdviser={true}
            link="/Adviser/Events"
          />
        </div>
      </div>

      <Testimonials isAdviser={true} />
      <ContactUs isAdviser={true} />
      <Footer isAdviser={true} />
    </div>
  );
};

export default RosemountInMediaPage;
